// ============================================
// Form.js
// フォーム関連のユーティリティ集
//
// エクスポートする関数:
//   1. addAutoFunc  : 郵便番号から住所を自動入力（yubinbango.js 連携）
//   2. numKeyFunc   : 数字入力フィールドに数字キーボードを表示
//   3. confirmForm  : Contact Form 7 の確認画面フロー制御
// ============================================

// ============================================
// 設定値
// ============================================
const CONFIG = {
  // --- addAutoFunc（yubinbango 住所自動入力）---

  // CF7 フォームのセレクター
  formSelector: '.wpcf7 form',

  // 郵便番号入力フィールドの name 属性
  zipFieldName: 'zip',

  // 住所入力フィールドの name 属性
  addressFieldName: 'address',

  // プライバシーポリシーのチェックボックス name（確認画面の表示ラベルに使用）
  privacyFieldName: 'privacy',
  privacyCheckedLabel: '同意する',
  privacyUncheckedLabel: '未同意',

  // --- numKeyFunc ---

  // 数字キーボードを表示したいフィールドのセレクター
  numInputSelector: '.js-num-input',
};

// ============================================
// 1. addAutoFunc — 郵便番号 → 住所の自動入力
//
// yubinbango.js（外部ライブラリ）を使って、郵便番号フィールドに
// 7桁の番号を入力すると住所フィールドへ自動反映する。
//
// 前提:
//   - enqueue.php で yubinbango.js が読み込まれていること
//   - CF7 フォームに name="zip" と name="address" が存在すること
//
// yubinbango.js は Microformat（HTML class 命名規則）を解釈して動作する。
// h-adr クラスをフォームに、p-postal-code / p-region 等を各フィールドに付与することで
// ライブラリが自動的に住所を補完する。
// ============================================
function addAutoFunc() {
  const form = document.querySelector(CONFIG.formSelector);
  const zipInput = document.querySelector(`[name="${CONFIG.zipFieldName}"]`);
  const addInput = document.querySelector(`[name="${CONFIG.addressFieldName}"]`);

  // 必要な要素と yubinbango.js の両方がある場合のみ初期化する
  if (!form || !zipInput || !addInput || !window.YubinBango?.MicroformatDom) return;

  // バインド済みチェック（二重初期化防止）
  if (form.dataset.yubinbangoBound === '1') return;
  form.dataset.yubinbangoBound = '1';

  // フォーム全体に Microformat のルートクラスを付与する
  form.classList.add('h-adr');

  // 国情報の hidden フィールドがなければ追加する（日本固定）
  if (!form.querySelector('.p-country-name')) {
    form.insertAdjacentHTML('afterbegin', '<input type="hidden" class="p-country-name" value="Japan">');
  }

  // 郵便番号フィールドに Microformat クラスを付与する
  zipInput.classList.add('p-postal-code');

  // 住所フィールドに Microformat クラスを付与する
  // yubinbango はこれらのクラスを持つフィールドに都道府県・市区町村・番地を分割して入力する
  addInput.classList.add(
    'p-region',          // 都道府県
    'p-locality',        // 市区町村
    'p-street-address',  // 番地
    'p-extended-address' // 建物名等
  );

  // yubinbango を初期化する（これにより自動補完が有効になる）
  new window.YubinBango.MicroformatDom();
}

// ============================================
// 2. numKeyFunc — 数字専用キーボード表示
//
// .js-num-input クラスを持つ input に inputmode="tel" を付与することで、
// モバイルで数字キーボード（テンキー）が表示されるようになる。
//
// inputmode="numeric" は一部端末で記号も含む IME になる場合があるため、
// 電話番号キーボード（tel）のほうが実用的。
// ============================================
function numKeyFunc() {
  const inputs = document.querySelectorAll(CONFIG.numInputSelector);
  if (inputs.length === 0) return;

  inputs.forEach((el) => {
    el.setAttribute('inputmode', 'tel');
  });
}

// ============================================
// 3. confirmForm — CF7 確認画面フロー制御
//
// Contact Form 7 に「確認画面」を追加するための JavaScript 制御。
// CF7 は標準では確認画面をサポートしないため、JS で画面を切り替える。
//
// HTML の構成（CF7 フォーム内に以下の data 属性を持つ要素を用意する）:
//
//   [data-inputs]     : 入力フォームのラッパー
//   [data-confirm]    : 確認内容の表示エリア
//   [data-to-confirm] : 「確認へ」ボタン
//   [data-back]       : 「戻る」ボタン
//   [data-send]       : 「送信」ボタン（CF7 の wpcf7-submit ではないカスタムボタン）
//   [data-to-send]    : 「送信」ボタンのラッパー
//   [data-field="フィールド名"] : 確認エリアに入力値を表示するスロット
//
// フロー:
//   入力 → 「確認へ」クリック → バリデーション → 確認表示 → 「送信」クリック → CF7 送信
//                               ↑「戻る」クリック ↓
// ============================================
function confirmForm() {
  // CF7 フィールド名に含まれる特殊文字を CSS セレクター用にエスケープする
  const escapeName = (name) =>
    window.CSS?.escape ? window.CSS.escape(name) : String(name).replace(/["\\]/g, '\\$&');

  // "name[]" の形式の配列名から [] を取り除く（data-field との照合用）
  const normalizeName = (name) => String(name || '').replace(/\[\]$/, '');

  document.querySelectorAll(CONFIG.formSelector).forEach((form) => {
    // バインド済みチェック（二重初期化防止）
    if (form.dataset.cf7ConfirmBound === '1') return;

    // 必要な要素を取得する
    const inputsWrap  = form.querySelector('[data-inputs]');
    const confirmWrap = form.querySelector('[data-confirm]');
    const toConfirmBtn = form.querySelector('[data-to-confirm]');
    const backBtn     = form.querySelector('[data-back]');
    const sendBtn     = form.querySelector('[data-send]');
    const sendWrap    = form.querySelector('[data-to-send]');
    const cf7Submits  = form.querySelectorAll('.wpcf7-submit'); // CF7 デフォルトの送信ボタン（複数可）

    // 必要な要素が1つでも欠けていれば初期化しない
    if (!inputsWrap || !confirmWrap || !toConfirmBtn || !backBtn || !sendBtn || cf7Submits.length === 0) return;
    form.dataset.cf7ConfirmBound = '1';

    // CF7 本来の送信ボタンは非表示にする（カスタム送信ボタンで代替する）
    cf7Submits.forEach((btn) => (btn.style.display = 'none'));

    // --- DOM 表示/非表示のユーティリティ ---

    const hide = (el) => {
      if (!el) return;
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    };

    const show = (el, display = '') => {
      if (!el) return;
      el.removeAttribute('hidden');
      el.style.display = display;
    };

    // --- 送信ボタンの有効/無効制御 ---

    const disableSending = () => {
      sendBtn.disabled = true;
      sendBtn.setAttribute('aria-busy', 'true');
      cf7Submits.forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      });
    };

    const enableSending = () => {
      sendBtn.disabled = false;
      sendBtn.removeAttribute('aria-busy');
      cf7Submits.forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
    };

    // CF7 本来の送信ボタン（スピナー含む）の表示/非表示
    const hideCf7Submits = () => {
      cf7Submits.forEach((btn) => {
        hide(btn);
        const spinner = btn.nextElementSibling;
        if (spinner?.classList.contains('wpcf7-spinner')) spinner.style.display = 'none';
      });
    };

    const showCf7Submits = () => {
      cf7Submits.forEach((btn) => {
        show(btn);
        const spinner = btn.nextElementSibling;
        if (spinner?.classList.contains('wpcf7-spinner')) spinner.style.display = '';
      });
    };

    // --- 初期状態を設定する ---
    show(inputsWrap);
    show(toConfirmBtn);
    hide(confirmWrap);
    hide(sendWrap);
    hide(backBtn);
    hide(sendBtn);

    // ============================================
    // 入力バリデーション（「確認へ」クリック前に実行）
    //
    // aria-required="true" を含む CF7 の必須項目を検査し、
    // 未入力があれば reportValidity() でブラウザ標準のエラー表示をする。
    // ============================================
    const validateBeforeConfirm = () => {
      // カスタムバリデーションメッセージをリセット
      form.querySelectorAll('[name]').forEach((el) => el.setCustomValidity?.(''));

      const required = form.querySelectorAll(
        '[name][required], [name][aria-required="true"], .wpcf7-validates-as-required [name]'
      );

      const checkedGroups = new Set();
      let firstInvalid = null;

      required.forEach((el) => {
        const name = el.name;
        if (!name) return;

        if (el.type === 'checkbox' || el.type === 'radio') {
          // チェックボックス・ラジオは同名グループで1つでも選択されていれば OK
          if (checkedGroups.has(name)) return;
          checkedGroups.add(name);

          const group = form.querySelectorAll(`[name="${escapeName(name)}"]`);
          if (!Array.from(group).some((i) => i.checked)) {
            const head = group[0];
            head?.setCustomValidity?.('この項目は必須です');
            if (!firstInvalid) firstInvalid = head;
          }
        } else if (el.tagName === 'SELECT') {
          if (!el.value) {
            el.setCustomValidity('この項目は必須です');
            if (!firstInvalid) firstInvalid = el;
          }
        } else {
          if (!(el.value || '').trim()) {
            el.setCustomValidity('この項目は必須です');
            if (!firstInvalid) firstInvalid = el;
          }
        }
      });

      const isValid = form.reportValidity();
      if (!isValid && firstInvalid) firstInvalid.focus();
      return isValid;
    };

    // ============================================
    // 確認画面に入力値を流し込む
    //
    // フォームの各 [name] 要素の値を読み取り、
    // [data-field="フィールド名"] 要素に textContent として書き出す。
    // ============================================
    const populateConfirm = () => {
      form.querySelectorAll('[name]').forEach((el) => {
        const name = el.name;
        if (!name) return;

        const fieldName = normalizeName(name);
        let value = '';

        if (el.type === 'checkbox') {
          const checked = form.querySelectorAll(`input[name="${escapeName(name)}"]:checked`);
          // privacy チェックボックスは「同意する/未同意」のラベルに変換する
          if (fieldName === CONFIG.privacyFieldName) {
            value = checked.length ? CONFIG.privacyCheckedLabel : CONFIG.privacyUncheckedLabel;
          } else {
            value = Array.from(checked).map((i) => i.value).join(', ');
          }
        } else if (el.type === 'radio') {
          const checked = form.querySelector(`input[name="${escapeName(name)}"]:checked`);
          value = checked ? checked.value : '';
        } else if (el.tagName === 'SELECT') {
          value = el.value || '';
        } else {
          value = el.value ?? '';
        }

        // data-field 属性でフィールド名と紐付けられた表示スロットに値を書き込む
        const slot = form.querySelector(
          `[data-field="${escapeName(name)}"], [data-field="${escapeName(fieldName)}"]`
        );
        if (slot) slot.textContent = value;
      });
    };

    // ============================================
    // 画面切り替えのイベントリスナー
    // ============================================

    // 「確認へ」ボタン: バリデーション → 確認画面へ
    toConfirmBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!validateBeforeConfirm()) return;

      populateConfirm();
      hide(inputsWrap);
      show(confirmWrap);
      hide(toConfirmBtn);
      hideCf7Submits();
      show(sendWrap);
      show(backBtn);
      show(sendBtn);
    });

    // 「戻る」ボタン: 入力画面へ戻る
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      show(inputsWrap);
      hide(confirmWrap);
      show(toConfirmBtn);
      showCf7Submits();
      hide(sendWrap);
      hide(backBtn);
      hide(sendBtn);

      // 最初の入力要素にフォーカスを戻してアクセシビリティに配慮する
      const firstInput = inputsWrap.querySelector('input, textarea, select');
      firstInput?.focus();
    });

    // ============================================
    // CF7 イベント連携
    // ============================================

    // 送信開始時（wpcf7beforesubmit）: ボタンを無効化してダブル送信を防ぐ
    form.addEventListener('wpcf7beforesubmit', () => {
      disableSending();
    });

    // エラー発生時の共通処理（入力画面に戻してエラーを表示する）
    const handleError = (apiResponse) => {
      // 確認画面が表示中なら「戻る」処理を実行する
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();

      // CF7 が描画するエラーメッセージを [data-error] スロットにも転記する
      const msg = form.querySelector('.wpcf7-response-output')?.textContent?.trim();
      if (msg) {
        const errorBox = form.querySelector('[data-error]');
        if (errorBox) {
          errorBox.textContent = msg;
          errorBox.removeAttribute('hidden');
        }
      }

      // nonce 期限切れ等の場合はフォームを再初期化する
      const code = apiResponse?.code || apiResponse?.status;
      if (code === 'rest_invalid_nonce' || code === 'aborted') {
        try {
          window.wpcf7?.init?.(form);
        } catch {
          // 再初期化失敗は無視する
        }
      }

      enableSending();

      // エラーのある要素までスクロールしてフォーカスを移す
      const errorTarget =
        form.querySelector('.wpcf7-not-valid') ||
        form.querySelector('[aria-invalid="true"]') ||
        form.querySelector('.wpcf7-response-output');
      errorTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorTarget?.focus?.();
    };

    // 各種 CF7 エラーイベント
    form.addEventListener('wpcf7invalid', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7spam', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7mailfailed', (e) => handleError(e.detail?.apiResponse));

    // 送信結果の統合ハンドラ（成功・失敗を一本化）
    form.addEventListener('wpcf7submit', (e) => {
      const status = e.detail?.apiResponse?.status;

      if (status === 'mail_sent') {
        // 送信成功時 → サンクスページへ遷移する場合はここに記述する
        // 例: window.location.href = '/thanks/';
      } else {
        handleError(e.detail?.apiResponse);
      }

      enableSending(); // 念のためボタンを有効に戻す
    });

    // フォームリセット時（例: CF7 がリセットするとき）は入力画面に戻す
    form.addEventListener('reset', () => {
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();
      enableSending();
    });
  });
}

export { addAutoFunc, numKeyFunc, confirmForm };
